import React, { useState, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  InputBase,
  alpha,
  Paper,
  Popper,
  ClickAwayListener,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  VpnKey,
  Logout,
  Settings,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import NotificationBell from './NotificationBell';

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 70;

const ModernNavbar = ({ isCollapsed, toggleSidebar }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const searchInputRef = useRef(null);
  const open = Boolean(anchorEl);
  const searchOpen = searchResults.length > 0;

  // Define all searchable pages
  const searchablePages = [
    { label: 'Dashboard', path: '/dashboard', category: 'Dashboard' },
    { label: 'Lake Dashboard', path: '/lakedashboard', category: 'Dashboard' },
    { label: 'Users', path: '/users', category: 'Admin' },
    { label: 'Roles & Permissions', path: '/roles', category: 'Admin' },
    { label: 'Departments', path: '/departments', category: 'Admin' },
    { label: 'Sites', path: '/sites', category: 'Sites' },
    { label: 'Vendors', path: '/contractor', category: 'Vendors' },
    { label: 'Agreements', path: '/agreement', category: 'Vendors' },
    { label: 'Contract Type', path: '/contracttype', category: 'Vendors' },
    { label: 'Tasks', path: '/taskmanagement', category: 'Tasks' },
    { label: 'Workflows', path: '/workflows', category: 'Tasks' },
    { label: 'SLA', path: '/sla', category: 'Tasks' },
    { label: 'Eco Assets', path: '/ecological', category: 'AI Intelligence' },
    { label: 'Lake Management', path: '/lake', category: 'AI Intelligence' },
    { label: 'Profile', path: '/profile', category: 'Account' },
  ];

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchValue(value);

    if (value.trim()) {
      const filtered = searchablePages.filter((page) =>
        page.label.toLowerCase().includes(value.toLowerCase()) ||
        page.category.toLowerCase().includes(value.toLowerCase())
      );
      setSearchResults(filtered.slice(0, 6)); // Limit to 6 results
    } else {
      setSearchResults([]);
    }
  };

  const handleSearchSelect = (path) => {
    navigate(path);
    setSearchValue('');
    setSearchResults([]);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter' && searchResults.length > 0) {
      handleSearchSelect(searchResults[0].path);
    } else if (event.key === 'Escape') {
      setSearchResults([]);
      searchInputRef.current?.blur();
    }
  };

  const handleClickAway = () => {
    setSearchResults([]);
  };

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth)
      .then(() => {
        navigate('/login');
      })
      .catch((error) => {
        console.error('Sign out error:', error);
      });
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        top: 0,
        left: isMobile ? 0 : (isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH),
        right: 0,
        width: isMobile ? '100%' : `calc(100% - ${isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH}px)`,
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: 1,
        borderBottom: 1,
        borderColor: 'divider',
        transition: (theme) => theme.transitions.create(['left', 'width'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      }}
    >
      <Toolbar
        sx={{
          minHeight: '56px !important',
          height: '56px',
          px: 1.5,
          transition: (theme) => theme.transitions.create(['padding-left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* Mobile Menu Toggle */}
        {isMobile && (
          <IconButton
            color="inherit"
            edge="start"
            onClick={toggleSidebar}
            size="small"
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Search Bar */}
        <ClickAwayListener onClickAway={handleClickAway}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: 4,
              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
              },
              '&:focus-within': {
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.15),
                boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
              mr: 2,
              ml: 0,
              width: '100%',
              maxWidth: 400,
              height: '36px',
              transition: 'all 0.2s ease',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            <Box
              sx={{
                p: (theme) => theme.spacing(0, 1.5),
                height: '100%',
                position: 'absolute',
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <SearchIcon />
            </Box>
            <InputBase
              ref={searchInputRef}
              placeholder="Search pages..."
              value={searchValue}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              inputProps={{ 'aria-label': 'search' }}
              sx={{
                color: 'inherit',
                width: '100%',
                height: '36px',
                '& .MuiInputBase-input': {
                  p: (theme) => theme.spacing(0.75, 1, 0.75, 0),
                  pl: (theme) => `calc(1em + ${theme.spacing(3.5)})`,
                  transition: (theme) => theme.transitions.create('width'),
                  width: '100%',
                  fontSize: '0.875rem',
                  '&:focus': {
                    width: { md: '35ch' },
                  },
                },
              }}
            />

            {/* Search Results Popper */}
            <Popper
              open={searchOpen}
              anchorEl={searchInputRef.current}
              placement="bottom-start"
              sx={{ zIndex: theme.zIndex.modal + 1 }}
            >
              <Paper
                elevation={3}
                sx={{
                  mt: 1,
                  minWidth: 300,
                  maxHeight: 400,
                  overflow: 'auto',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.12))',
                }}
              >
                <Box sx={{ p: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    SEARCH RESULTS
                  </Typography>
                </Box>
                {searchResults.map((result) => (
                  <Box
                    key={result.path}
                    onClick={() => handleSearchSelect(result.path)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {result.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {result.category}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Popper>
          </Box>
        </ClickAwayListener>

        <Box sx={{ flexGrow: 1 }} />

        {/* Right Side Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Notifications */}
          <NotificationBell />

          {/* User Avatar */}
          <IconButton
            onClick={handleProfileClick}
            size="small"
            sx={{ 
              ml: 0.5,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <Avatar
              alt="User"
              src="/assets/images/user-icon.png"
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
        </Box>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.12))',
              mt: 1.5,
              minWidth: 220,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1.5,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem
            onClick={() => {
              handleClose();
              navigate('/profile');
            }}
          >
            <Avatar alt="User" src="/assets/images/user-icon.png" />
            <Box>
              <Typography variant="body2" fontWeight={600}>
                My Profile
              </Typography>
              <Typography variant="caption" color="text.secondary">
                View and edit profile
              </Typography>
            </Box>
          </MenuItem>
          
          <Divider />
          
          <MenuItem onClick={handleClose}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
          
          <MenuItem onClick={handleClose}>
            <ListItemIcon>
              <VpnKey fontSize="small" />
            </ListItemIcon>
            Reset Password
          </MenuItem>
          
          <Divider />
          
          <MenuItem
            onClick={() => {
              handleClose();
              handleLogout();
            }}
          >
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default ModernNavbar;
